import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { FormProvider, RHFSelect } from 'src/components/hook-form';
import CkEditorInput from 'src/components/CkEditorInput';
import useLocales from 'src/hooks/useLocales';
import { ITermsAndCondition } from 'src/@types/Choqorak/Settings/TermsAndCondition';
import {
  IContractInfo,
  IContractInfoDetails,
} from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import useThrottle from 'src/hooks/useThrottle';
const contractInfo: IContractInfoDetails = {
  contractNumber: '123',
  contractStartDate: new Date(),
  contractEndDate: new Date(),
  discountStartDate: new Date(),
  discountEndDate: new Date(),
  contractDuration: 'test',
  finalRentPerMonth: 900,
  advancePayment: 2000,
  customerPashtoName: 'John Doe',
  customerFatherName: 'John Doe',
  customerGrandFatherName: 'test',
  customerPhone: 'test',
  customerTazkiraNo: 'test',
  customerTazkiraJoldNo: 'test',
  customerTazkiraPageNo: 'test',
  customerTazkiraRegNo: 'test',
  customerMainProvencePashto: 'test',
  customerMainDistrictPashto: 'test',
  customerMainAddress: 'test',
  customerCurrentProvencePashto: 'test',
  customerCurrentDistrictPashto: 'test',
  shopTypePashto: 'test',

  customerCurrentAddress: 'test',
  shopCode: 'SHOP001',
  floorCode: 'SHOP001',
  blockName: 'SHOP001',
  contractType: 'Fixed',

  totalContractDurationRent: 24,
  totalContractDurationPaidRent: 12,
  refundedAdvancePayment: 500,
  contractStatus: 'Active',
  terminationDescription: '',
  terminationDate: new Date(),
  settlmentDeductionAmount: 50,
  deductedAmountFromAdvancePayment: 100,
  manuallyPaidAmount: 500,
  remainAmount: 1000,
  deductionDescription: 'Early Payment Discount',
  totalContractDurationRentWithoutDiscount: 0,
  contractCreatorPashtoName: 'test',
  contractCreatorFatherPashtoName: 'test',
  pashtoGrandFatherName: 'test',

  rentCarrencyPashto: 'test',
};

const FormTypes = [
  { type: 0, value: 'NEW_CONTRACT' },
  { type: 1, value: 'EXTEND_CONTRACT' },
  { type: 2, value: 'TERMINATE_CONTRACT' },
  { type: 3, value: 'END_CONTRACT' },
  { type: 4, value: 'DISCOUNT_DOC' },
];

export default observer(function TermsAndConditionNewEditForm() {
  const { TermsAndConditionStore } = useStore();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const {
    createTermsAndCondition,
    updateTermsAndCondition,
    editMode,
    selectedTermsAndCondition,
    clearSelectedTermsAndCondition,
  } = TermsAndConditionStore;

  const navigate = useNavigate();

  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [openDrawer, setOpenDrawer] = useState(false); // State to control the Drawer visibility
  // const [contractInfo, setContractInfo] = useState<IContractInfo | null>(null); // State to store contract info

  const NewTermsAndConditionSchema = Yup.object().shape({
    termAndConditionDescreption: Yup.string().required(
      `${translate('Validation.termAndConditionDescreption')}`
    ),
  });

  const defaultValues = useMemo<ITermsAndCondition>(
    () => ({
      termAndConditionId: selectedTermsAndCondition?.termAndConditionId,
      termAndConditionDescreption: selectedTermsAndCondition?.termAndConditionDescreption || '',
      type: selectedTermsAndCondition?.type || undefined,
    }),
    [selectedTermsAndCondition]
  );

  const methods = useForm<ITermsAndCondition>({
    resolver: yupResolver(NewTermsAndConditionSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle((data: ITermsAndCondition) => {
    // Example hardcoded value
    if (!data.termAndConditionId) {
      createTermsAndCondition(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.TermsAndCondition.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    } else {
      updateTermsAndCondition(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.TermsAndCondition.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.title != null) {
            setError('afterSubmit', { ...err, message: json.title });
          }
        });
    }
  });

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // Function to open the drawer and set contract info (Example)
  const openContractDrawer = () => {
    setOpenDrawer(true);
  };

  const insertAtCursor = (text: string) => {
    if (editorInstance) {
      const currentData = editorInstance.getData();
      editorInstance.setData(`${currentData}${text}`);
    }
  };

  // Extract the keys of IContractInfo to display as the list items
  const contractInfoKeys = Object.keys(contractInfo) as (keyof IContractInfoDetails)[];

  // Log the keys to inspect the data

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFSelect name="type" label={translate('TermAndCondition.FormType')}>
                <option value="" />
                {FormTypes.map((op, index) => (
                  <option key={index} value={op.type}>
                    {op.value}
                  </option>
                ))}
              </RHFSelect>
            </Box>
            <CkEditorInput
              name="termAndConditionDescreption"
              onReady={(editor) => setEditorInstance(editor)}
              sx={{ marginTop: 2, color: 'black' }}
            />
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedTermsAndCondition();
                  navigate(PATH_DASHBOARD.TermsAndCondition.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>

              {/* Add Button to Open the Drawer */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={openContractDrawer} // Open drawer
                startIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
              >
                Open Contract Info
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Drawer Component */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)} // This will close the drawer
        sx={{
          width: 400,
          '& .MuiDrawer-paper': {
            width: 400,
            padding: 2,
          },
        }}
      >
        <Box>
          <h3>Contract Information</h3>
          <List>
            {contractInfoKeys.map((key) => (
              <List key={key}>{key}</List>
            ))}
          </List>
          <Button onClick={() => setOpenDrawer(false)}>Close Drawer</Button>
        </Box>
      </Drawer>
    </FormProvider>
  );
});
